import { PRESET_NAMES, type PresetName } from '../settings/presets.js';
import type { Settings } from '../settings/schema.js';
import { parseBackendOverride, type BackendName } from './backend.js';

/** Query-string overrides used by the e2e tests and the frame-time bench. */
export interface UrlOptions {
  settings: Partial<Settings>;
  backend: BackendName | null;
  /** Keyboard-only, no pointer lock, deterministic camera. */
  e2e: boolean;
  /** Scripted movement for benchmarking. */
  autopilot: boolean;
  /**
   * `render=0` skips drawing (simulation, prediction, HUD and events still run): headless
   * software rendering costs hundreds of milliseconds a frame, which an online client cannot
   * afford when the server keeps real time.
   */
  render: boolean;
  /** Simulated link (`lag`, `jitter`, `loss`); honoured in development and e2e only. */
  net: { lagMs: number; jitterMs: number; loss: number } | null;
  /** A deliberately broken client for the anti-cheat test (`cheat=speed`); e2e only. */
  cheat: 'speed' | null;
  /** `particles=cpu` steps the particle buffers on the CPU instead of the compute kernel. */
  particles: 'gpu' | 'cpu';
  /** `stage=1` keeps the Deploy gate that e2e and autopilot runs otherwise skip. */
  stage: boolean;
}

function nonNegative(q: URLSearchParams, key: string, max: number): number | null {
  if (!q.has(key)) return null;
  const v = Number(q.get(key));
  return Number.isFinite(v) && v >= 0 && v <= max ? v : null;
}

export function parseUrlOptions(search: string): UrlOptions {
  const q = new URLSearchParams(search);
  const settings: Partial<Settings> = {};
  const preset = q.get('preset');
  if (preset !== null && (PRESET_NAMES as readonly string[]).includes(preset)) {
    settings.preset = preset as PresetName;
  }
  const scale = Number(q.get('scale'));
  if (q.has('scale') && Number.isFinite(scale) && scale >= 0.5 && scale <= 1) {
    settings.renderScale = scale;
  }
  const drs = q.get('drs');
  if (drs === '0' || drs === '1') settings.dynamicResolution = drs === '1';
  const fps = q.get('fps');
  if (fps === '0' || fps === '60' || fps === '120') settings.fpsCap = Number(fps) as 0 | 60 | 120;
  const stats = q.get('stats');
  if (stats === '0' || stats === '1') settings.showStats = stats === '1';
  const e2e = q.get('e2e') === '1';
  const lagMs = nonNegative(q, 'lag', 5000);
  const jitterMs = nonNegative(q, 'jitter', 2000);
  const loss = nonNegative(q, 'loss', 1);
  const net =
    lagMs !== null || jitterMs !== null || loss !== null
      ? { lagMs: lagMs ?? 0, jitterMs: jitterMs ?? 0, loss: loss ?? 0 }
      : null;
  return {
    settings,
    backend: parseBackendOverride(search),
    e2e,
    autopilot: q.get('autopilot') === '1',
    render: q.get('render') !== '0',
    net,
    cheat: e2e && q.get('cheat') === 'speed' ? 'speed' : null,
    particles: q.get('particles') === 'cpu' ? 'cpu' : 'gpu',
    stage: q.get('stage') === '1',
  };
}

export interface SettingsTarget {
  setPreset(preset: PresetName): void;
  update(partial: Partial<Settings>): void;
}

/**
 * Applies URL overrides to the settings store. `preset` goes through `setPreset` so the preset's
 * quality fields (render scale, shadows, effects) are applied, not just the label; the remaining
 * fields then override on top (e.g. `?preset=ultra&drs=0`).
 */
export function applyUrlSettings(target: SettingsTarget, settings: Partial<Settings>): void {
  const { preset, ...rest } = settings;
  if (preset !== undefined) target.setPreset(preset);
  if (Object.keys(rest).length > 0) target.update(rest);
}
